import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useSwyftxCredential } from "@/hooks/usePortfolioReport";
import { PushNotificationButton } from "@/components/PushNotificationButton";

export function SwyftxReportSettings() {
  const { data: cred, refetch } = useSwyftxCredential();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  // Link an already-subscribed device to this account.
  useEffect(() => {
    (async () => {
      try {
        const reg = await navigator.serviceWorker?.getRegistration();
        const sub = await (reg as any)?.pushManager?.getSubscription();
        if (sub) await supabase.functions.invoke("save-swyftx-key", { body: { action: "link_push", endpoint: sub.endpoint } });
      } catch { /* push unsupported */ }
    })();
  }, []);

  const call = async (body: Record<string, unknown>) => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("save-swyftx-key", { body });
    setBusy(false);
    if (error || data?.error) {
      let msg = data?.error;
      try { msg = msg ?? (await (error as any)?.context?.json())?.error; } catch { /* noop */ }
      toast.error(msg ?? "Something went wrong.");
      return false;
    }
    return true;
  };

  const save = async () => {
    if (await call({ action: "save", apiKey: key })) {
      setKey(""); toast.success("Key saved. Your first report arrives at 6am — or run it now."); refetch();
    }
  };
  const remove = async () => {
    if (await call({ action: "delete" })) { toast.success("Key removed. Daily reports stopped."); refetch(); qc.invalidateQueries({ queryKey: ["portfolio-reports"] }); }
  };
  const toggle = async (v: boolean) => {
    await supabase.from("user_swyftx_credentials").update({ report_enabled: v }).not("user_id", "is", null);
    refetch();
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4" /> Daily Swyftx portfolio report</CardTitle></CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          Every morning at 6am (Sydney) we read your live Swyftx holdings and prices and write a personal portfolio review.
          Use a <strong>read-only</strong> key from Swyftx → Profile → API, with no IP whitelist. It's stored encrypted and never shown again.
        </p>
        {cred ? (
          <div className="space-y-3">
            <p>Key ending in <span className="type-data-sm">…{cred.key_last4}</span> is saved.</p>
            <div className="flex items-center gap-2">
              <Switch checked={cred.report_enabled} onCheckedChange={toggle} id="rep" />
              <label htmlFor="rep">Send me the daily report</label>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild size="sm"><Link to="/my-report">Open my report</Link></Button>
              <Button size="sm" variant="outline" onClick={remove} disabled={busy} className="gap-1"><Trash2 className="w-4 h-4" /> Remove key</Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input type="password" placeholder="Swyftx read-only API key" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off" />
            <Button onClick={save} disabled={busy || key.trim().length < 20}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
          </div>
        )}
        <div className="pt-2 border-t border-border/50 space-y-2">
          <p className="text-muted-foreground">Turn on notifications on this device (and on your phone if you've installed the app) to get a ping when the report is ready.</p>
          <PushNotificationButton />
        </div>
      </CardContent>
    </Card>
  );
}
